import { useEffect, useMemo, useRef, useState } from 'react';
import {
  approvalMode,
  designPassOutstanding,
  HAND_OFF_NOTE_MAX_LENGTH,
  isDesignPassStalled,
  isReviewable,
  listening,
  RISK_LEVEL_WORDS,
  type Agent,
  type Task,
  type TaskComment,
  type TaskEvent,
  type TaskKind,
  type TaskPr,
} from '@shared/domain.ts';
import type { Deploy } from '@/data/selectors.ts';
import { agentPulse, ciPulse, inProduction, isQuiet, previewDeploy, primaryAction } from '@/data/selectors.ts';
import { useNow, useRigg } from '@/data/RiggProvider.tsx';
import { api } from '@/lib/api.ts';
import { ago, clock, cn, initial, prettyUrl } from '@/lib/format.ts';
import { useAttachments } from '@/lib/uploads.ts';
import { AgentPulseChip, CiPulseChip, GitHubMark, PrChip } from './Pulses.tsx';
import { AttachButton, AttachmentPills } from './Attachments.tsx';
import { Markdown } from './Markdown.tsx';
import { Shot } from './Shot.tsx';
import { Lightbox } from './Lightbox.tsx';
import {
  Button,
  Dialog,
  Empty,
  Field,
  KebabMenu,
  MenuItem,
  SectionLabel,
  Select,
  Textarea,
  Tip,
  useToast,
} from './ui.tsx';
import { KindPill, kindOptions, originText } from './TaskFacts.tsx';
import { buildTaskMarkdown } from '@/lib/exportMarkdown.ts';
import { OpenQuestions } from './Questions.tsx';
import { TaskFollowUps } from './FollowUps.tsx';

/**
 * What to show in *Where things stand* when the routine has not written a
 * `status_note`.
 *
 * "Nothing from the routine yet" is only true if nothing ever arrived. A
 * routine that said what it was doing and then went quiet has told us two
 * useful things — what it was doing, and when it last said so — and reporting
 * silence instead reads as a dispatch that never landed, which sends somebody
 * off to debug the wrong end of the system.
 */
function whereItStands(task: Task, live: boolean, now: number): string {
  const doing = task.agent_activity;
  const at = task.agent_activity_at ?? task.agent_heartbeat_at;

  // A booked window is the one case where silence has already been explained,
  // so say what it is doing and when it owes us a word — otherwise the panel
  // reports a gap the routine warned us about as though nobody knew.
  if (isQuiet(task, now)) {
    return (
      `No written update yet — the routine is ${doing ?? 'working'}, ` +
      `and said it would be quiet until about ${clock(task.agent_quiet_until)}.`
    );
  }
  if (live) return `No written update yet — the routine is ${doing ?? 'working'}.`;
  if (at && doing) return `No written update. At ${clock(at)} the routine was ${doing}; nothing since.`;
  if (at) return `No written update. Nothing from the routine since ${clock(at)}.`;
  return 'Nothing from the routine yet.';
}

/**
 * The detail pane.
 *
 * Ordered by what a person actually wants, in the order they want it: what is
 * happening right now, then the argument (comments) — the one control on this
 * view reached for on every visit, not just the ones with something to look
 * at — then something to look at, and only then the paperwork.
 */
export function TaskDetail({
  task,
  onClose,
  onDeleted,
}: {
  task: Task;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const {
    data,
    me,
    project,
    projects,
    isAdminOfTask,
    openThread,
    loadMoreComments,
    addOptimisticComment,
    removeOptimisticComment,
  } = useRigg();
  const now = useNow();
  const { notify } = useToast();

  const scroller = useRef<HTMLDivElement>(null);
  const commentBox = useRef<HTMLTextAreaElement>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [handOffOpen, setHandOffOpen] = useState(false);
  const [handOffNote, setHandOffNote] = useState('');

  const {
    items: attachments,
    onDrop,
    onDragOver,
    onPaste,
    attach,
    remove: removeAttachment,
    clear: clearAttachments,
    restore: restoreAttachments,
    compose,
    uploading,
    hasAttachment,
  } = useAttachments({ projectId: task.project_id, notify });

  useEffect(() => {
    openThread(task.id);
    if (scroller.current) scroller.current.scrollTop = 0;
  }, [task.id, openThread]);

  const comments = data.comments[task.id] ?? [];
  // Newest first — that is the one someone opening the task actually wants to
  // read. The store holds the thread oldest-first (that is what paging older
  // comments in relies on), so the reverse happens here, at render.
  const latestFirst = useMemo(() => [...comments].reverse(), [comments]);
  const totalComments = data.commentCounts[task.id] ?? comments.length;
  // Same reasoning as comments: a routine appends as it goes, so the most
  // recent screenshot — the one that answers "does it look right now" — is
  // last in the array and first on screen.
  const shotsLatestFirst = useMemo(() => [...task.images].reverse(), [task.images]);
  const moreComments = Math.max(0, totalComments - comments.length);
  const events = data.events[task.id] ?? [];
  const agent = task.agent_id ? data.agents[task.agent_id] : null;
  const repo = task.repo_id ? data.repos[task.repo_id] : null;
  const category = task.category_id ? data.categories[task.category_id] : null;

  const pulse = agentPulse(task, now);
  const ci = ciPulse(task);
  const taskSettings = data.settings[task.project_id];
  const preview = previewDeploy(task, data.ciRuns, taskSettings?.preview_workflow);
  const production = inProduction(task, data.ciRuns, taskSettings?.prod_workflow);
  const primary = primaryAction(task, isAdminOfTask(task));
  // Only for an adopted pull request — who, if anyone, hears a comment on it.
  const listener = listening(task, now);
  const handOffable =
    listener?.who === 'nobody' &&
    isAdminOfTask(task) &&
    Boolean(task.pr_url) &&
    task.pr_state !== 'merged' &&
    task.pr_state !== 'closed' &&
    !task.archived_at &&
    !task.deleted_at;

  // Recomputed on the same tick as everything else on this panel, so a pass
  // that goes quiet crosses into "no word" without needing a reload.
  const stalled = isDesignPassStalled(task, now);

  const designWorker = useMemo(
    () =>
      (Object.values(data.agents) as Agent[]).find(
        (agent) => agent.project_id === task.project_id && agent.kind === 'design',
      ) ?? null,
    [data.agents, task.project_id],
  );

  // Falls back to `project` (set whenever a single project is active) so
  // this still resolves the moment the task loads, before `projects` — only
  // needed on the "All projects" board — has necessarily caught up.
  const taskProject = projects.find((p) => p.id === task.project_id) ?? project;

  const links = useMemo(
    () => task.links.filter((link) => link.kind !== 'pr'),
    [task.links],
  );

  // Every pull request this task carries, not just the primary one — see the
  // `task_prs` migration. Sorted by number, which is also creation order on a
  // single repository and reads as "the order they were opened in".
  const prs = useMemo(
    () =>
      Object.values(data.taskPrs)
        .filter((pr) => pr.task_id === task.id)
        .sort((a, b) => a.number - b.number),
    [data.taskPrs, task.id],
  );

  // The pull requests a delete would leave open on GitHub — what the
  // confirmation asks about. Falls back to the primary columns for a task
  // recorded before `task_prs` existed.
  const openPrNumbers = useMemo(() => {
    if (prs.length > 0) {
      return prs.filter((pr) => pr.state === 'open' || pr.state === 'draft').map((pr) => pr.number);
    }
    return task.pr_url && task.pr_number && (task.pr_state === 'open' || task.pr_state === 'draft')
      ? [task.pr_number]
      : [];
  }, [prs, task.pr_url, task.pr_number, task.pr_state]);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (error) {
      notify(label, { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  const remove = (closePrs: boolean) =>
    run('Could not delete', async () => {
      const result = await api.remove(task.id, { closePrs });
      setDeleteOpen(false);
      for (const failure of result.pr_close_failed ?? []) {
        notify(`Deleted, but could not close #${failure.number}`, { body: failure.error, tone: 'bad' });
      }
      onDeleted();
    });

  const [shotAt, setShotAt] = useState<number | null>(null);

  const copyMarkdown = async () => {
    // Best effort: the brief is one more round trip than everything else
    // here, which is already in hand. Worth having, not worth blocking on —
    // a task copied without it is still a task copied.
    let brief: string | null = null;
    try {
      brief = await api.brief(task.id);
    } catch {
      // omitted below; nothing else in the export depends on it
    }

    const markdown = buildTaskMarkdown({
      task,
      appUrl: window.location.origin,
      projectName: taskProject?.name ?? null,
      repoLabel: repo ? `${repo.owner}/${repo.name}` : null,
      categoryLabel: category?.label ?? null,
      originLabel: originText(task, data),
      prs,
      comments, // oldest first, as the store already holds it
      totalComments,
      authorNameOf: (c) => authorOf(c, data.profiles),
      standing: task.status_note ?? whereItStands(task, pulse.live, now),
      brief,
    });

    try {
      await navigator.clipboard.writeText(markdown);
      notify(brief ? 'Copied task as Markdown' : 'Copied task as Markdown — brief could not be fetched');
    } catch (error) {
      notify('Could not copy to clipboard', { body: (error as Error).message, tone: 'bad' });
    }
  };

  const submitComment = async () => {
    const body = compose(comment.trim());
    if (!body) return;

    // Sent to the server as the comment's real id, so the confirmed row lands
    // under the same identity the optimistic one is already showing under —
    // see the matching comment in Composer's `send`.
    const id = crypto.randomUUID();
    const optimistic: TaskComment = {
      id,
      task_id: task.id,
      project_id: task.project_id,
      user_id: me?.id ?? null,
      agent_id: null,
      author_name: me?.name ?? null,
      origin: 'rigg',
      body,
      github_url: null,
      github_id: null,
      github_error: null,
      created_at: new Date().toISOString(),
    };

    const savedComment = comment;
    const savedAttachments = attachments;

    // Show it and clear the box now — the round trip, and any post to
    // GitHub, happen after. A failure below puts both back.
    setComment('');
    clearAttachments();
    addOptimisticComment(optimistic);

    setBusy(true);
    try {
      const result = await api.comment(task.id, body, id);
      // Same id as the placeholder — refreshes it in place with the
      // confirmed row rather than adding a second one.
      addOptimisticComment(result.comment);
    } catch (error) {
      removeOptimisticComment(optimistic);
      setComment(savedComment);
      restoreAttachments(savedAttachments);
      notify('Could not post the comment', { body: (error as Error).message, tone: 'bad' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-0 w-full flex-col">
      {/* ── header ─────────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b border-line px-4 pt-4 pb-4 sm:px-6 sm:pt-5">
        <div className="flex items-center gap-2.5">
          <StatusPill task={task} />
          <KindPill kind={task.kind} variant="dark" />
          <span className="truncate text-[12px] font-medium text-mute">
            {taskProject?.name} · #{task.ref} · {originText(task, data)}
          </span>
          <span className="flex-1" />
          <KebabMenu>
            <MenuItem onSelect={() => void copyMarkdown()}>Copy task as Markdown</MenuItem>
          </KebabMenu>
          <button
            onClick={onClose}
            title="Close details"
            className="rg-focus grid size-7 shrink-0 place-items-center rounded-full text-soft hover:bg-btn hover:text-hi2"
          >
            ✕
          </button>
        </div>

        <h2 className="mt-2.5 text-[17.5px] leading-snug font-[650] tracking-[-0.01em] text-hi">
          {task.title}
        </h2>
        {/*
          The pull request's own title, when it says something the task's does
          not — by the time a routine opens one it has usually named the change
          more precisely than whoever asked for it.
        */}
        {task.pr_title && task.pr_title.trim() !== task.title.trim() ? (
          <a
            href={task.pr_url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="mt-1 flex min-w-0 items-center gap-1.5 text-[13px] text-soft hover:text-t1"
            title="The pull request's title"
          >
            <GitHubMark className="size-3.5 shrink-0" />
            <span className="truncate">
              {task.pr_title}
              {task.pr_number ? <span className="text-mute"> #{task.pr_number}</span> : null}
            </span>
          </a>
        ) : null}

        {/*
          What the requester actually said, verbatim and in their own language
          — open, never collapsed: the brief is a routine's reading of it, and
          this is what to hold that reading up against. Board-only; no routine
          ever receives it.
        */}
        {task.quote ? (
          <figure className="mt-3">
            <figcaption className="mb-1 text-[11px] font-semibold text-mute">What they said</figcaption>
            <blockquote className="border-l-2 border-btnbd pl-3 text-[13.5px] leading-relaxed whitespace-pre-wrap text-t1">
              {task.quote}
            </blockquote>
          </figure>
        ) : null}

        {/* the two states, given the room they deserve */}
        <div className="mt-3.5 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-line bg-panel px-3.5 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-[11px] font-semibold text-mute">Agent</span>
            <AgentPulseChip task={task} now={now} variant="dark" />
          </div>
          <span className="h-4 w-px bg-line" aria-hidden />
          <div className="flex min-w-0 items-center gap-2">
            <span className="text-[11px] font-semibold text-mute">GitHub</span>
            {task.branch ? (
              <CiPulseChip task={task} now={now} variant="dark" />
            ) : (
              <span className="text-[12.5px] text-mute">no branch yet</span>
            )}
          </div>
          {prs.length > 1 ? (
            <>
              <span className="h-4 w-px bg-line" aria-hidden />
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-semibold text-mute">
                  {prs.length} pull requests
                </span>
                {prs.map((pr) => (
                  <PrLink key={pr.id} pr={pr} />
                ))}
              </div>
            </>
          ) : task.pr_url ? (
            <>
              <span className="h-4 w-px bg-line" aria-hidden />
              <PrChip task={task} variant="dark" />
            </>
          ) : null}
          {preview.state !== 'none' ? (
            <>
              <span className="h-4 w-px bg-line" aria-hidden />
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-[11px] font-semibold text-mute">Preview</span>
                <DeployChip deploy={preview} now={now} />
              </div>
            </>
          ) : null}
          {production.state !== 'none' || task.merged_at ? (
            <>
              <span className="h-4 w-px bg-line" aria-hidden />
              <div className="flex min-w-0 items-center gap-2">
                <span className="text-[11px] font-semibold text-mute">Production</span>
                <DeployChip deploy={production} now={now} />
              </div>
            </>
          ) : null}
        </div>

        {listener ? (
          <p
            className={cn(
              'mt-2 text-[12.5px]',
              listener.who === 'nobody' ? 'font-semibold text-warnx' : 'text-mute',
            )}
          >
            {listener.word}
          </p>
        ) : null}

        <div className="mt-4 flex flex-wrap gap-2">
          {handOffable ? (
            <Tip content="A routine continues on this pull request's branch, from a brief built from its description, changes and review comments. It never opens a new pull request.">
              <Button tone="primary" disabled={busy} onClick={() => setHandOffOpen(true)}>
                Hand to a routine
              </Button>
            </Tip>
          ) : null}
          {primary === 'approve' ? (
            <Button
              tone="primary"
              disabled={busy}
              title={
                approvalMode(task) === 'auto_merge' && !task.merge_held_by
                  ? 'Still in progress — this turns auto-merge on, and Rigg lands it once it is done, green and reviewed'
                  : undefined
              }
              onClick={() => run('Could not approve', () => api.approve(task.id))}
            >
              {approvalMode(task) === 'auto_merge' && !task.merge_held_by
                ? 'Approve — auto-merge when ready'
                : 'Approve merge'}
            </Button>
          ) : null}
          {primary === 'sign-off' ? (
            <Button tone="primary" disabled={busy} onClick={() => run('Could not sign off', () => api.signOff(task.id))}>
              Sign off
            </Button>
          ) : null}
          {primary === 'retry' ? (
            <Button tone="primary" disabled={busy} onClick={() => run('Could not resend', () => api.redispatch(task.id))}>
              Send it again
            </Button>
          ) : null}

          <Button
            disabled={busy}
            onClick={async () => {
              try {
                const brief = await api.brief(task.id);
                await navigator.clipboard.writeText(brief);
                setCopied(true);
                setTimeout(() => setCopied(false), 1400);
              } catch (error) {
                notify('Could not copy the brief', { body: (error as Error).message, tone: 'bad' });
              }
            }}
          >
            {copied ? 'Copied ✓' : 'Copy brief'}
          </Button>

          {/*
            Only a routine asks for a pass, so the one thing a person does with
            one is stop it — it is holding the merge, and until this existed a
            worker that died left the pull request wedged with nothing anywhere
            able to clear it.
          */}
          {designPassOutstanding(task) ? (
            <Tip content="Ends the pass and releases the merge. Use it when the worker has died holding this, or you no longer want the pass.">
              <Button
                disabled={busy}
                onClick={() =>
                  run('Could not stop the design pass', async () => {
                    await api.cancelDesignPass(task.id);
                    notify('Design pass stopped');
                  })
                }
              >
                Stop design pass
              </Button>
            </Tip>
          ) : null}

          {task.pr_url ? (
            <Tip content="Re-read the pull request's state, and deliver anything that has not reached it">
              <Button
                disabled={busy}
                onClick={() =>
                  run('Could not reach GitHub', async () => {
                    const result = await api.syncGithub(task.id);
                    notify(
                      result.posted_comments
                        ? `Up to date — ${result.posted_comments} comment(s) delivered`
                        : 'Up to date with GitHub',
                    );
                  })
                }
              >
                <GitHubMark /> Refresh
              </Button>
            </Tip>
          ) : null}

          {task.deleted_at ? (
            <Button
              disabled={busy}
              onClick={() => run('Could not restore', () => api.restore(task.id))}
            >
              Restore
            </Button>
          ) : (
            <>
              <Tip
                content={
                  task.snoozed_at
                    ? 'Bring it back into the working queues'
                    : 'Park it for later — drops out of the working queues and stops asking anyone to review it'
                }
              >
                <Button
                  disabled={busy}
                  onClick={() =>
                    run(task.snoozed_at ? 'Could not unsnooze' : 'Could not snooze', () =>
                      task.snoozed_at ? api.unsnooze(task.id) : api.snooze(task.id),
                    )
                  }
                >
                  {task.snoozed_at ? 'Unsnooze' : 'Snooze'}
                </Button>
              </Tip>
              <Button
                tone="danger"
                disabled={busy}
                onClick={() =>
                  openPrNumbers.length > 0 ? setDeleteOpen(true) : void remove(false)
                }
              >
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* ── body ───────────────────────────────────────────────────────── */}
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto px-4 pt-5 pb-8 sm:px-6">
        <OpenQuestions task={task} />

        <SectionLabel className="mb-2.5">
          Where things stand
          {task.status_note && task.status_note_at ? (
            <span className="font-normal text-mute"> · {ago(task.status_note_at, now)}</span>
          ) : null}
        </SectionLabel>
        <div
          className={cn(
            'rounded-xl border px-4 py-3.5 text-[13.5px] leading-relaxed',
            task.status === 'error'
              ? 'border-warnx/26 bg-warnx/8 text-t3'
              : task.agent_state === 'waiting_on_you' ||
                  (task.agent_state === 'ready_for_review' && isReviewable(task))
                ? 'border-amberx/28 bg-amberx/8 text-t3'
                : 'border-line bg-callout text-t3',
            !task.status_note && 'text-soft',
          )}
        >
          {task.status_note ? <Markdown>{task.status_note}</Markdown> : whereItStands(task, pulse.live, now)}
        </div>

        {/*
          Said out loud, because a design pass is the one thing here that holds
          a merge without anything looking wrong. A pull request sitting green
          and approved with no explanation is the shape of a stalled board.
        */}
        {designPassOutstanding(task) ? (
          <div className="mt-2.5 rounded-lg border border-violet/25 bg-violet/8 px-3 py-2 text-[12.5px] leading-relaxed text-t3">
            {task.design_state === 'running'
              ? `Design pass running${designWorker ? ` on ${designWorker.name}` : ''}. The merge is held until it finishes.`
              : 'Design pass queued. The merge is held until it finishes.'}
            {task.design_then === 'resume'
              ? ' The routine has stopped and is sent this task again once the pass is over.'
              : null}
            {task.design_message ? (
              <span className="block text-soft">The routine asked for: {task.design_message}</span>
            ) : null}
            {/*
              The pass's own line, which used to be the routine's and was
              overwritten by it. Stalled is said out loud here rather than left
              to be inferred from a timestamp: a pass that has stopped talking
              is holding the merge exactly as hard as one that is working.
            */}
            {task.design_activity ? (
              <span className="mt-1 block">
                {task.design_activity}
                <span className="text-soft">
                  {' · '}
                  {stalled ? 'no word for ' : ''}
                  {ago(task.design_activity_at ?? task.design_claimed_at)}
                </span>
              </span>
            ) : null}
            {task.design_note ? (
              <span className="mt-1 block text-soft">{task.design_note}</span>
            ) : null}
          </div>
        ) : task.design_state === 'error' && task.design_error ? (
          <div className="mt-2.5 rounded-lg border border-warnx/25 bg-warnx/6 px-3 py-2 text-[12.5px] leading-relaxed text-warnx">
            Design pass failed — {task.design_error}
          </div>
        ) : null}

        {task.last_error ? (
          <div className="mt-2.5 rounded-lg border border-warnx/25 bg-warnx/6 px-3 py-2 font-mono text-[11.5px] leading-relaxed text-warnx">
            {task.last_error}
          </div>
        ) : null}

        {/*
          ── comments ───────────────────────────────────────────────────
          Second thing on the page, not last: the form is the one control on
          this whole view somebody reaches for on every visit, so it sits
          right under the status instead of below screenshots, links and
          details. The form leads, comments follow, newest first — say
          something, or read what was just said. Older history is a click
          away, not a scroll.
        */}
        <SectionLabel className="mt-6 mb-2.5">Comments</SectionLabel>

        <div
          className="relative"
          onDragEnter={(event) => {
            if (event.dataTransfer.types.includes('Files')) setDragging(true);
          }}
          onDragOver={onDragOver}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node)) setDragging(false);
          }}
          onDrop={(event) => {
            setDragging(false);
            onDrop(event);
          }}
        >
          <Textarea
            ref={commentBox}
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                void submitComment();
              }
            }}
            onPaste={onPaste}
            placeholder="Message the routine… drag in files, or paste a screenshot"
            className="min-h-16"
            aria-label="Add a comment"
          />
          {dragging ? (
            <div className="rg-anim pointer-events-none absolute inset-0 grid place-items-center rounded-[10px] border-2 border-dashed border-accent bg-app/90 text-[13px] font-semibold text-accent">
              Drop to attach
            </div>
          ) : null}
        </div>
        <AttachmentPills items={attachments} onRemove={removeAttachment} />
        <div className="mt-2 flex flex-wrap items-center gap-2 sm:flex-nowrap sm:gap-3">
          <AttachButton onFiles={attach} />
          {/*
            The pull request is the delivery mechanism, not a copy of the
            conversation — so whether one exists is the whole difference between
            a message that reaches the routine and one that only reaches the
            board. Say which, rather than implying it went somewhere.

            Ordered last and full-width below `sm`: it is a full sentence with
            nowhere near enough room next to two buttons on a phone, so it
            drops to its own line instead of squeezing into a sliver between
            them.
          */}
          <span className="order-last w-full text-[12px] text-mute sm:order-none sm:w-auto">
            {task.pr_number
              ? `Posted on pull request #${task.pr_number} — that is how the routine sees it.`
              : 'No pull request yet, so the routine cannot see this. It stays on the task.'}
          </span>
          <span className="flex-1" />
          <Button
            tone="primary"
            size="sm"
            disabled={busy || uploading || (!comment.trim() && !hasAttachment)}
            onClick={() => void submitComment()}
          >
            {uploading ? 'Uploading…' : 'Comment'}
          </Button>
        </div>

        {comments.length === 0 ? (
          <div className="mt-3 text-[13px] text-mute">No comments yet.</div>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {latestFirst.map((entry) => (
              <CommentRow key={entry.id} comment={entry} authorName={authorOf(entry, data.profiles)} now={now} />
            ))}
            {moreComments > 0 ? (
              <Button
                tone="ghost"
                size="sm"
                className="self-start"
                disabled={loadingMore}
                onClick={async () => {
                  setLoadingMore(true);
                  try {
                    await loadMoreComments(task.id);
                  } catch (error) {
                    notify('Could not load older comments', { body: (error as Error).message, tone: 'bad' });
                  } finally {
                    setLoadingMore(false);
                  }
                }}
              >
                {loadingMore
                  ? 'Loading…'
                  : `Show ${Math.min(moreComments, 10)} older comment${Math.min(moreComments, 10) === 1 ? '' : 's'}`}
              </Button>
            ) : null}
          </div>
        )}

        {task.images.length > 0 ? (
          <>
            <SectionLabel className="mt-6 mb-2.5">
              Screenshots <span className="font-normal text-faint">· {task.images.length}</span>
            </SectionLabel>
            <div className="grid grid-cols-2 gap-2.5">
              {shotsLatestFirst.map((image, index) => (
                <figure key={image.url} className="m-0 min-w-0">
                  <button
                    type="button"
                    onClick={() => setShotAt(index)}
                    aria-label={`Open ${image.label ?? 'screenshot'} full size`}
                    className="rg-focus block w-full cursor-zoom-in"
                  >
                    <Shot
                      url={image.url}
                      label={image.label}
                      variant="dark"
                      className="aspect-[16/10] w-full transition-[filter] hover:brightness-110"
                    />
                  </button>
                  {image.label ? (
                    <figcaption className="mt-1.5 text-[11.5px] leading-snug text-soft">
                      {image.label}
                    </figcaption>
                  ) : null}
                </figure>
              ))}
            </div>
          </>
        ) : null}

        {Object.values(data.followUps).some((f) => f.task_id === task.id) ? (
          <>
            <SectionLabel className="mt-6 mb-2.5">Suggested follow-ups</SectionLabel>
            <TaskFollowUps task={task} />
          </>
        ) : null}

        {links.length > 0 ? (
          <>
            <SectionLabel className="mt-6 mb-2.5">Links</SectionLabel>
            <div className="flex flex-col gap-2">
              {links.map((link) => (
                <div key={link.url} className="grid grid-cols-[126px_1fr] items-baseline gap-3">
                  <span className="text-[12.5px] font-medium text-soft">{link.label}</span>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block truncate text-[13px]"
                  >
                    {prettyUrl(link.url)}
                  </a>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {/*
          Only what has an answer. A column of "not yet" and "not set" is a list
          of things that have not happened, which is longer than the list of
          things that have and reads as though something is missing. Created is
          always true, so it always shows.
        */}
        <SectionLabel className="mt-6 mb-2.5">Details</SectionLabel>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[126px_1fr] items-center gap-3">
            <span className="text-[12.5px] font-medium text-soft">Kind</span>
            <span>
              <Select
                size="sm"
                value={task.kind ?? 'none'}
                onChange={(value) =>
                  void api
                    .setKind(task.id, value === 'none' ? null : (value as TaskKind))
                    .catch((error: Error) => notify('Could not change the kind', { body: error.message, tone: 'bad' }))
                }
                options={kindOptions('Not set')}
                aria-label="Kind"
              />
            </span>
          </div>
          <Kv k="Origin" v={originText(task, data)} />
          {agent ? <Kv k="Routine" v={agent.name} /> : null}
          {repo ? <Kv k="Repository" v={`${repo.owner}/${repo.name}`} mono /> : null}
          {task.branch ? <Kv k="Branch" v={task.branch} mono accent /> : null}
          {category ? <Kv k="Category" v={category.label} /> : null}
          {ci.tone !== 'none' ? <Kv k="Checks" v={ci.detail ?? ci.word} /> : null}
          {task.auto_merge ? <Kv k="Auto-merge" v="on" accent /> : null}
          {task.smart ? <Kv k="Smart" v="on" accent /> : null}
          {task.signed_off_at ? (
            <Kv
              k="Signed off"
              v={`${data.profiles[task.signed_off_by ?? '']?.name ?? 'yes'}, ${ago(task.signed_off_at, now)}`}
            />
          ) : null}
          {task.approved_at ? (
            <Kv
              k="Approved"
              v={`${data.profiles[task.approved_by ?? '']?.name ?? 'yes'}, ${ago(task.approved_at, now)}`}
            />
          ) : null}
          {task.merge_waived_by ? (
            <Kv k="Approval" v={`waived by “${task.merge_waived_by}”`} accent />
          ) : null}
          {task.merge_held_by ? <Kv k="Merge" v={task.merge_held_by} /> : null}
          {task.risk ? (
            <Kv
              k="Risk"
              v={
                `${task.risk.level} ${RISK_LEVEL_WORDS[task.risk.level]} · UX ${task.risk.ux_impact} · ` +
                `${task.risk.lines_changed} line${task.risk.lines_changed === 1 ? '' : 's'}` +
                (task.risk.issue_ref !== null ? ` · #${task.risk.issue_ref}` : '')
              }
            />
          ) : null}
          {task.risk?.issue_gist ? <Kv k="Issue" v={task.risk.issue_gist} /> : null}
          {task.risk?.fix_gist ? <Kv k="Fix" v={task.risk.fix_gist} /> : null}
          <Kv k="Created" v={ago(task.created_at, now)} />
          {task.reopened_at ? <Kv k="Sent back" v={ago(task.reopened_at, now)} /> : null}
        </div>

        <SectionLabel className="mt-6 mb-2.5">What you asked for</SectionLabel>
        <div className="rounded-xl border border-line bg-brief px-3.5 py-3 text-[13px] leading-relaxed whitespace-pre-wrap text-t6">
          {task.prompt}
        </div>

        <SectionLabel className="mt-6 mb-2.5">History</SectionLabel>
        {events.length === 0 ? (
          <div className="text-[13px] text-mute">Nothing recorded yet.</div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {[...events].reverse().map((event) => (
              <EventRow key={event.id} event={event} profiles={data.profiles} me={me?.id ?? null} />
            ))}
          </div>
        )}
      </div>

      <Dialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={openPrNumbers.length === 1 ? 'Close the pull request too?' : 'Close the pull requests too?'}
        description={
          <>
            This task still has{' '}
            {openPrNumbers.length === 1
              ? `an open pull request, #${openPrNumbers[0]}`
              : `open pull requests: ${openPrNumbers.map((n) => `#${n}`).join(', ')}`}
            . Deleting the task leaves {openPrNumbers.length === 1 ? 'it' : 'them'} open on GitHub unless
            you close {openPrNumbers.length === 1 ? 'it' : 'them'} here. Restoring the task later does not
            reopen {openPrNumbers.length === 1 ? 'it' : 'them'}.
          </>
        }
        footer={
          <>
            <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button disabled={busy} onClick={() => void remove(false)}>
              Delete, keep open
            </Button>
            <Button tone="danger" disabled={busy} onClick={() => void remove(true)}>
              Delete and close
            </Button>
          </>
        }
      />

      <Dialog
        open={handOffOpen}
        onOpenChange={setHandOffOpen}
        title="Hand to a routine"
        description="A routine continues on this pull request's branch, from a brief built from its description, changes and review comments. Anything you write here leads that brief."
        footer={
          <>
            <Button onClick={() => setHandOffOpen(false)}>Cancel</Button>
            <Button
              tone="primary"
              disabled={busy || handOffNote.trim().length > HAND_OFF_NOTE_MAX_LENGTH}
              onClick={() =>
                void run('Could not hand it to a routine', async () => {
                  const result = await api.handOff(task.id, { comment: handOffNote });
                  setHandOffOpen(false);
                  setHandOffNote('');
                  if (result.error) notify('Not sent yet', { body: result.error, tone: 'bad' });
                })
              }
            >
              Hand it over
            </Button>
          </>
        }
      >
        <Field
          label="Comment (optional)"
          hint={
            handOffNote.trim().length > HAND_OFF_NOTE_MAX_LENGTH
              ? `${handOffNote.trim().length - HAND_OFF_NOTE_MAX_LENGTH} characters too long`
              : 'Why it stalled, what to do first, what to leave alone.'
          }
        >
          <Textarea
            rows={5}
            value={handOffNote}
            onChange={(event) => setHandOffNote(event.target.value)}
            placeholder="e.g. Only the mobile review comment is left — fix that and land it."
          />
        </Field>
      </Dialog>

      {shotAt !== null ? (
        <Lightbox
          images={shotsLatestFirst}
          index={shotAt}
          onIndex={setShotAt}
          onClose={() => setShotAt(null)}
          onComment={(body) => api.comment(task.id, body)}
          projectId={task.project_id}
        />
      ) : null}
    </div>
  );
}

function authorOf(comment: TaskComment, profiles: Record<string, { name: string }>): string {
  if (comment.user_id && profiles[comment.user_id]) return profiles[comment.user_id]!.name;
  return comment.author_name ?? 'someone';
}

function CommentRow({
  comment,
  authorName,
  now,
}: {
  comment: TaskComment;
  authorName: string;
  now: number;
}) {
  return (
    <div className="flex gap-2.5">
      <span
        className={cn(
          'mt-px grid size-[22px] shrink-0 place-items-center rounded-full text-[11px] font-[650]',
          comment.origin === 'agent' ? 'bg-goodx/18 text-goodx' : 'bg-avatar text-t6',
        )}
      >
        {initial(authorName)}
      </span>
      <div className="min-w-0">
        <div className="text-[12.5px]">
          <span className="font-[650] text-t1">{authorName}</span>
          <span className="text-mute"> · {ago(comment.created_at, now)}</span>
          {comment.origin === 'github' ? (
            <span className="text-mute"> · from GitHub</span>
          ) : null}
          {comment.github_url ? (
            <>
              {' · '}
              <a href={comment.github_url} target="_blank" rel="noreferrer" className="text-[12px]">
                on the PR
              </a>
            </>
          ) : null}
          {comment.github_error ? (
            <Tip content={comment.github_error}>
              <span className="text-[12px] text-warnx"> · not posted to GitHub</span>
            </Tip>
          ) : null}
        </div>
        <div className="mt-0.5 text-[13px] leading-relaxed text-t6">
          {/*
            Only the routine's own replies are agent-authored prose — a
            person's comment through this same textarea, and anything synced
            in from GitHub, keep their exact whitespace instead.
          */}
          {comment.origin === 'agent' ? (
            <Markdown>{comment.body}</Markdown>
          ) : (
            <div className="whitespace-pre-wrap">{comment.body}</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A deploy, said in as few words as it takes.
 *
 * Deliberately not a traffic light: "deployed 4m ago" answers the question,
 * where a green dot only tells you that something is fine without saying what
 * or when — and "when" is most of what people want from a deploy.
 */
function DeployChip({ deploy, now }: { deploy: Deploy; now: number }) {
  const tone =
    deploy.state === 'passing'
      ? 'text-goodx'
      : deploy.state === 'failing'
        ? 'text-warnx'
        : deploy.state === 'running'
          ? 'text-amberx'
          : 'text-mute';

  const body = (
    <span className={cn('truncate text-[12.5px]', tone)}>
      {deploy.word}
      {deploy.at ? <span className="text-mute"> · {ago(deploy.at, now)}</span> : null}
    </span>
  );

  return deploy.url ? (
    <a
      href={deploy.url}
      target="_blank"
      rel="noreferrer"
      // No background to show for it, so the padding is invisible — it only
      // exists to grow the tap target past the bare text's height.
      className="-my-1 min-w-0 rounded py-1 no-underline"
    >
      {body}
    </a>
  ) : (
    body
  );
}

/**
 * One entry in the "N pull requests" cluster — the same idea as `PrChip`,
 * compressed to fit several side by side, and reading a `TaskPr` row rather
 * than the task's own single `pr_url`/`pr_number`/`pr_state`.
 */
function PrLink({ pr }: { pr: TaskPr }) {
  return (
    <Tip content={pr.title ? `${pr.title} — ${pr.state}` : `Pull request is ${pr.state}`}>
      <a
        href={pr.url}
        target="_blank"
        rel="noreferrer"
        onClick={(event) => event.stopPropagation()}
        className={cn(
          'rg-focus inline-flex items-center gap-1 rounded-full border border-btnbd bg-btn px-2 py-[3px] text-[11.5px] font-semibold whitespace-nowrap text-t2 no-underline hover:bg-btnhover hover:text-hi2 hover:no-underline',
          pr.state === 'merged' && 'text-violetx',
          pr.state === 'closed' && 'opacity-60',
        )}
      >
        <GitHubMark />#{pr.number}
      </a>
    </Tip>
  );
}

const EVENT_TONE: Record<string, string> = {
  created: 'bg-mute',
  dispatched: 'bg-soft',
  dispatch_failed: 'bg-warnx',
  queued: 'bg-mute',
  status: 'bg-goodx',
  activity: 'bg-amberx',
  links: 'bg-goodx',
  images: 'bg-goodx',
  comment: 'bg-violetx',
  reopened: 'bg-amberx',
  stalled: 'bg-warnx',
  pr: 'bg-violetx',
  ci: 'bg-warnx',
  note: 'bg-mute',
};

const EVENT_WORD: Record<string, string> = {
  created: 'Created',
  dispatched: 'Sent',
  dispatch_failed: 'Not delivered',
  queued: 'Queued',
  status: 'Status',
  activity: 'Working',
  links: 'Links',
  images: 'Screenshots',
  comment: 'Comment',
  reopened: 'Sent back',
  stalled: 'Went quiet',
  pr: 'Pull request',
  ci: 'Checks',
  note: 'Update',
  kind: 'Kind',
};

function EventRow({
  event,
  profiles,
  me,
}: {
  event: TaskEvent;
  profiles: Record<string, { name: string }>;
  me: string | null;
}) {
  const who =
    event.actor === 'user'
      ? event.actor_user_id === me
        ? 'you'
        : (profiles[event.actor_user_id ?? '']?.name ?? 'someone')
      : event.actor === 'agent'
        ? 'the routine'
        : event.actor === 'github'
          ? 'GitHub'
          : 'Rigg';

  return (
    <div className="flex items-baseline gap-3">
      <span className="w-10 shrink-0 font-mono text-[11.5px] text-mute tabular-nums">
        {clock(event.at)}
      </span>
      <span
        className={cn('relative -top-px size-[7px] shrink-0 rounded-full', EVENT_TONE[event.kind] ?? 'bg-mute')}
        aria-hidden
      />
      <span className="min-w-0 text-[13px] leading-relaxed text-body">
        <span className="font-[650] text-t1">{EVENT_WORD[event.kind] ?? event.kind}</span>
        {event.message ? <> — {event.message}</> : null}
        <span className="text-mute"> ({who})</span>
      </span>
    </div>
  );
}

function Kv({
  k,
  v,
  dim,
  mono,
  accent,
}: {
  k: string;
  v: string;
  dim?: boolean;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div className="grid grid-cols-[126px_1fr] items-baseline gap-3">
      <span className="text-[12.5px] font-medium text-soft">{k}</span>
      <span
        className={cn(
          'min-w-0 truncate',
          mono ? 'font-mono text-[12.5px]' : 'text-[13px]',
          accent ? 'text-accent' : dim ? 'text-soft' : 'text-t1',
        )}
      >
        {v}
      </span>
    </div>
  );
}

function StatusPill({ task }: { task: Task }) {
  const map: Record<string, [string, string]> = {
    fresh: ['Queued', 'bg-pill text-t7'],
    working: ['Working', 'bg-amberx/16 text-amberx'],
    signed_off: ['Signed off', 'bg-goodx/16 text-goodx'],
    approved: ['Approved ✓', 'bg-accent text-ink'],
    merged: ['Merged', 'bg-pill text-t7'],
    deployed: ['Live', 'bg-accent text-ink'],
    error: ['Failed', 'bg-warnx/16 text-warnx'],
  };
  const [text, className] = map[task.status] ?? ['—', 'bg-pill text-t7'];
  return (
    <span className={cn('shrink-0 rounded-full px-2.5 py-[3px] text-[11.5px] font-semibold', className)}>
      {text}
    </span>
  );
}

export { Empty };
